import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { contentStyles, paragraphRegular } from '@helpers/typography';
import ButtonStyles from '@stories/Components/Buttons/Button/Button.styles';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';
const groupColourVars = css `
  --bgHover: linear-gradient(
    90deg,
    ${rgba(brand.primary.orange, 0.16)} 0%,
    ${rgba(brand.primary.orangeDark, 0.16)} 100%
  );
  --borderColour: ${brand.primary.orangeDark};
  --bgHoverColour: ${rgba(brand.primary.orange, 0.16)};
  --iconBgColour: linear-gradient(
    90deg,
    ${brand.primary.orange} 0%,
    ${brand.primary.orangeDark} 100%
  );
  --iconFgColour: ${brand.primary.white};
`;
const Container = styled.a `
  ${ButtonReset};
  ${transition('border-color')};
  ${fonts.poppins.extraBold};
  ${groupColourVars};

  display: flex;
  width: 100%;
  position: relative;
  align-items: center;
  gap: ${fluid(8, 16)};
  color: ${brand.primary.black};
  font-size: 16px;
  text-align: left;
  text-decoration: none;

  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    width: 100%;
    background: var(--bgHover);
    opacity: 0;
    z-index: -1;
    border-radius: 3px;
  }

  > svg {
    ${transition('transform, color')};

    display: block;
    width: 10px;
    transform: rotate(-90deg);
    margin-left: auto;
  }

  &:focus {
    outline: none;
  }

  &:focus,
  &:hover {
    > svg {
      transform: translateX(2px) rotate(-90deg);
    }
  }

  &[data-active='true'],
  &:focus,
  &:hover {
    &::after {
      opacity: 1;
    }

    > svg {
      color: var(--borderColour);
    }
  }

  &[data-active='true'] {
    border-color: var(--borderColour);
  }

  /* Mobile click styles - button should mimic press */
  @media ${until(Device.DesktopSmall)} {
    ${transition('transform', 0.15)};

    &:active {
      transform: translateY(1.5px);
    }
  }

  @media ${from(Device.DesktopSmall)} {
    border: 3px solid transparent;
    border-radius: 4px;
    padding: 12px 18px 12px 16px;
  }
`;
const GroupIcon = styled.span `
  width: ${fluid(32, 40)};
  padding: 8px;
  aspect-ratio: 1;
  background: var(--iconBgColour);
  color: var(--iconFgColour);
  border-radius: 8px;

  svg {
    display: block;
  }

  @media ${from(Device.DesktopSmall)} {
    border-radius: 4px;
  }
`;
const GroupLinkBlock = styled.div `
  ${groupColourVars};

  display: flex;
  gap: ${fluid(16, 20)};
  padding: ${fluid(8, 20)};
  background: var(--bgHover);
  border-radius: 4px;

  ${GroupIcon} {
    width: ${fluid(46, 56)};
    margin: auto 0;
  }
`;
const GroupLinkContent = styled.div `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${fluid(8, 16)};
  flex: 1;

  ${ButtonStyles.Container} {
    &:hover ${ButtonStyles.IconWrapper} {
      transform: translateX(3px);
    }
  }

  ${ButtonStyles.IconWrapper} {
    ${transition('transform, color', 0.25)};

    svg {
      transform: rotate(-90deg);
    }
  }

  @media ${until(Device.DesktopSmall)} {
    ${ButtonStyles.IconWrapper} {
      --iconHeight: 7px;
    }

    ${ButtonStyles.Container} {
      --bgColourHover: transparent;

      padding: 0;
      height: auto;
    }

    ${ButtonStyles.Text} {
      font-size: 12px;
      text-underline-offset: 2px !important;
    }
  }

  @media ${from(Device.DesktopSmall)} {
    flex-direction: row;
    align-items: center;

    ${ButtonStyles.IconWrapper} {
      --iconHeight: 10px;
    }

    ${ButtonStyles.Container} {
      margin-left: auto;
      white-space: nowrap;
    }
  }
`;
const GroupLinkTitle = styled.p `
  ${paragraphRegular};

  font-weight: 800;
  margin-bottom: 0;

  @media ${until(Device.DesktopSmall)} {
    display: none;
  }
`;
const GroupLinkDescription = styled.div `
  ${contentStyles};

  margin: 0;

  p {
    font-size: ${fluid(13, 14)};
    line-height: 20px;
  }

  p,
  ol li,
  ul li {
    margin-bottom: ${fluid(6, 8)};
  }

  ol,
  ul {
    margin-top: ${fluid(8, 10)};
    margin-bottom: ${fluid(8, 10)};
  }

  @media ${from(Device.DesktopSmall)} {
    margin-top: 4px;
  }
`;
const GroupLinkStyles = {
    Container,
    GroupLinkBlock,
    GroupIcon,
    GroupLinkContent,
    GroupLinkTitle,
    GroupLinkDescription,
};
export default GroupLinkStyles;
